import { MessageType, PushNotificationEventType } from '@/enums';
import type { InputLiveStatusResponse } from '@/models/input';
import { FuelToolsApiService } from '@/services/FuelToolsApi/fuelToolsApi.service';

export interface LiveInputStatusUpdatePayload {
  inputId: string;
  overallStatus?: string;
}

const readOverallStatus = (data?: Record<string, unknown> | null): string | undefined => {
  if (!data) {
    return undefined;
  }
  return (data.overallStatus ?? data.OverallStatus ?? data.status) as string | undefined;
};

export const parseLiveInputStatusUpdateMessage = (message: unknown): LiveInputStatusUpdatePayload | null => {
  if (!message || typeof message !== 'object') {
    return null;
  }

  const msg = message as { messageType?: string; data?: Record<string, unknown> };
  if (msg.messageType !== MessageType.Notification || !msg.data) {
    return null;
  }

  if (msg.data.eventType !== PushNotificationEventType.InputStatusUpdate) {
    return null;
  }

  const inputId = (msg.data.id ?? msg.data.inputId) as string | undefined;
  if (!inputId) {
    return null;
  }

  return {
    inputId,
    overallStatus: readOverallStatus(msg.data),
  };
};

export const resolveLiveInputStatusUpdate = async (
  payload: LiveInputStatusUpdatePayload
): Promise<InputLiveStatusResponse & { inputId: string }> => {
  if (payload.overallStatus != null) {
    return {
      inputId: payload.inputId,
      overallStatus: payload.overallStatus,
    };
  }

  const response = (await FuelToolsApiService.getInputStreamStatus(payload.inputId)) as Record<string, unknown> | null;
  return {
    inputId: payload.inputId,
    overallStatus: readOverallStatus(response ?? undefined),
    liveProgramIds: (response?.liveProgramIds as string[] | undefined) ?? undefined,
    currentLiveProgramIds: (response?.currentLiveProgramIds as string[] | undefined) ?? undefined,
  };
};

/** Map API overallStatus to badge variant. Recording displays as Live. */
export const toSourceBadgeStatus = (overallStatus?: string | null): string | undefined => {
  const normalized = overallStatus?.toLowerCase().trim();
  if (!normalized) {
    return undefined;
  }

  if (normalized === 'recording') {
    return 'live';
  }

  if (normalized === 'connected') {
    return 'connected';
  }

  if (normalized === 'disconnected' || normalized === 'no connection') {
    return 'disconnected';
  }

  if (normalized === 'error' || normalized === 'failed') {
    return 'error';
  }

  // Live program schedule states (LiveProgramDto.ScheduleDisplay.Status).
  if (normalized === 'scheduled') {
    return 'scheduled';
  }

  if (normalized === 'notscheduled' || normalized === 'not scheduled') {
    return 'notscheduled';
  }

  return undefined;
};

export type InputLiveStatusSnapshot = {
  inputId: string;
  overallStatus?: string;
  liveProgramIds: string[];
};

type LiveStatusListener = (status: InputLiveStatusSnapshot) => void;

const statusByInputId = new Map<string, InputLiveStatusSnapshot>();
const listenersByInputId = new Map<string, Set<LiveStatusListener>>();
const inflightByInputId = new Map<string, Promise<InputLiveStatusSnapshot>>();

let currentOuId: string | undefined;

const readLiveProgramIds = (data?: Record<string, unknown> | null): string[] => {
  if (!data || !Array.isArray(data.liveProgramIds)) {
    return [];
  }

  return data.liveProgramIds.filter((id): id is string => typeof id === 'string' && id.length > 0);
};

const notifyListeners = (status: InputLiveStatusSnapshot) => {
  statusByInputId.set(status.inputId, status);
  listenersByInputId.get(status.inputId)?.forEach((listener) => listener(status));
};

const isUnassignedInputId = (inputId: string | undefined, ouId?: string): boolean => Boolean(inputId && ouId && inputId === ouId);

export const setLiveInputStatusOuId = (ouId?: string) => {
  currentOuId = ouId;
};

export const getCachedInputLiveStatus = (inputId: string): InputLiveStatusSnapshot | undefined => statusByInputId.get(inputId);

export const toSubscribedInputIds = (inputIds: string[], ouId?: string): string[] =>
  Array.from(new Set(inputIds.filter((inputId) => inputId && !isUnassignedInputId(inputId, ouId)))).sort();

export const isUnassignedLiveInputId = (inputId: string | undefined, ouId?: string): boolean =>
  isUnassignedInputId(inputId, ouId);

export const fetchInputLiveStatusSnapshot = async (inputId: string): Promise<InputLiveStatusSnapshot> => {
  const existing = inflightByInputId.get(inputId);
  if (existing) {
    return existing;
  }

  const request = (async () => {
    const response = (await FuelToolsApiService.getInputStreamStatus(inputId)) as Record<string, unknown> | null;
    const snapshot: InputLiveStatusSnapshot = {
      inputId,
      overallStatus: readOverallStatus(response ?? undefined),
      liveProgramIds: readLiveProgramIds(response ?? undefined),
    };

    notifyListeners(snapshot);
    return snapshot;
  })();

  inflightByInputId.set(inputId, request);
  try {
    return await request;
  } finally {
    inflightByInputId.delete(inputId);
  }
};

export const subscribeToInputLiveStatus = (inputId: string, listener: LiveStatusListener) => {
  if (!listenersByInputId.has(inputId)) {
    listenersByInputId.set(inputId, new Set());
  }

  listenersByInputId.get(inputId)?.add(listener);

  const current = statusByInputId.get(inputId);
  if (current) {
    listener(current);
  }

  return () => {
    listenersByInputId.get(inputId)?.delete(listener);
  };
};

export const refreshInputLiveStatus = (inputId: string) => {
  fetchInputLiveStatusSnapshot(inputId).catch((error) => {
    console.error('Failed to fetch input live status', { inputId, error });
  });
};

/** Replay cache into subscribers; fetch any id that has no snapshot yet. */
export const replayCachedInputLiveStatus = (inputIds: string[]) => {
  inputIds.forEach((inputId) => {
    const cached = statusByInputId.get(inputId);
    if (cached) {
      notifyListeners(cached);
      return;
    }

    refreshInputLiveStatus(inputId);
  });
};

/** Process one notification-socket frame. Safe to call for every message; no-ops unless subscribed. */
export const handleLiveInputStatusSocketMessage = (message: unknown) => {
  try {
    const payload = parseLiveInputStatusUpdateMessage(message);
    if (!payload?.inputId || isUnassignedInputId(payload.inputId, currentOuId)) {
      return;
    }

    if (!listenersByInputId.get(payload.inputId)?.size) {
      return;
    }

    refreshInputLiveStatus(payload.inputId);
  } catch (error) {
    console.error('Failed to handle live input status socket message', error);
  }
};
